import { Icon } from "@iconify/react";
import { Dialog } from "@mui/material";
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLocation,
  useNavigate,
  Navigate,
  UNSAFE_NavigationContext as NavigationContext,
} from "react-router-dom";
import { toast } from "react-toastify";
import { BulkResignationEmployeeOption } from "../../api/Esop";
import { useBulkCompleteResignation } from "../../queries/employees";
import { useGetEmailSettings } from "../../queries/siteSettings";
import { formatDisplayDate } from "../../utils/date";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../components/utils";
import Avatar from "../esopOverview/Avatar";
import AlertDialog from "../../components/shared/AlertDialog";
import { cn } from "../../components/shared/Card";
import { EmployeeForResignation } from "../../components/shared/BulkCompleteResignationDialog";
import { Select } from "../../components/shared/Select";
import { Label } from "../../components/shared/InputField";
import BCHeader from "../../shared/BCHeader";
import "../milestones/milestone.css";

type OptionRow = {
  employeeId: string;
  employeeName: string;
  employeeIdentificationString: string;
  resignationDate: string;
  lastWorkingDay: string;
  transferAccessToPersonalEmail: boolean;
  regenerateCredentials: boolean;
  personalEmail: string;
};

function BulkCompleteResignations() {
  const location = useLocation();
  const employees: EmployeeForResignation[] = location.state?.employees;

  if (!employees || employees.length === 0) {
    return <Navigate to="/options/allEmployees" replace />;
  }

  return <BulkCompleteResignationsContent employees={employees} />;
}

function BulkCompleteResignationsContent({
  employees,
}: {
  employees: EmployeeForResignation[];
}) {
  const navigate = useNavigate();
  const gridApiRef = useRef<GridApi | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDiscardDialog, setShowDiscardDialog] = useState(false);
  const [showLeaveGuard, setShowLeaveGuard] = useState(false);
  const pendingNavRef = useRef<(() => void) | null>(null);
  const isLeavingRef = useRef(false);
  const { navigator } = useContext(NavigationContext);
  const [selectedCount, setSelectedCount] = useState(employees.length);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [bulkTransfer, setBulkTransfer] = useState(false);
  const [bulkRegenerate, setBulkRegenerate] = useState(false);
  const [failedEmployees, setFailedEmployees] = useState<
    { employeeId: string; reason: string }[]
  >([]);
  const [hasEmailAction, setHasEmailAction] = useState(false);
  const { mutate: bulkCompleteResignation, isLoading } =
    useBulkCompleteResignation();
  const { data: emailSettings } = useGetEmailSettings();

  useEffect(() => {
    const nav = navigator as any;
    const originalPush = nav.push?.bind(nav);
    const originalReplace = nav.replace?.bind(nav);

    const intercept =
      (original: (...args: any[]) => void) =>
      (...args: any[]) => {
        if (isLeavingRef.current) {
          original(...args);
        } else {
          pendingNavRef.current = () => {
            isLeavingRef.current = true;
            original(...args);
          };
          setShowLeaveGuard(true);
        }
      };

    if (originalPush) nav.push = intercept(originalPush);
    if (originalReplace) nav.replace = intercept(originalReplace);

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (originalPush) nav.push = originalPush;
      if (originalReplace) nav.replace = originalReplace;
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [navigator]);

  const handleLeave = () => {
    setShowLeaveGuard(false);
    isLeavingRef.current = true;
    pendingNavRef.current?.();
    pendingNavRef.current = null;
  };

  const handleStay = () => {
    setShowLeaveGuard(false);
    pendingNavRef.current = null;
  };

  const rowData = useMemo<OptionRow[]>(
    () =>
      employees.map((e) => ({
        employeeId: e.id,
        employeeName: e.employeeName,
        employeeIdentificationString: e.employeeIdentificationString ?? "—",
        resignationDate:
          e.resignationDate &&
          formatDisplayDate(e.resignationDate) !== "Invalid Date"
            ? formatDisplayDate(e.resignationDate)
            : "—",
        lastWorkingDay:
          e.lastWorkingDay &&
          formatDisplayDate(e.lastWorkingDay) !== "Invalid Date"
            ? formatDisplayDate(e.lastWorkingDay)
            : "—",
        transferAccessToPersonalEmail: false,
        regenerateCredentials: false,
        personalEmail: e.personalEmail ?? "",
      })),
    [employees]
  );

  const EmployeeRenderer = useCallback(
    (params: ICellRendererParams<OptionRow>) => (
      <HStack className="items-center h-full mt-1">
        <Avatar name={params.data?.employeeName ?? ""} />
        <Box className="ml-2">
          <p className="text-xs font-medium text-gray-dark">
            {params.data?.employeeName}
          </p>
          <p className="text-xxs text-gray-light">
            {params.data?.employeeIdentificationString}
          </p>
        </Box>
      </HStack>
    ),
    []
  );

  const columnDefs = useMemo<ColDef<OptionRow>[]>(
    () => [
      {
        headerName: "",
        field: "employeeId",
        pinned: "left",
        checkboxSelection: true,
        cellStyle: { paddingTop: "15px", lineHeight: "20px" },
        headerCheckboxSelection: true,
        width: 48,
        maxWidth: 48,
        minWidth: 48,
        resizable: false,
        suppressMovable: true,
        cellRenderer: () => null,
      },
      {
        headerName: "Employee",
        field: "employeeName",
        minWidth: 180,
        pinned: "left",
        cellRenderer: EmployeeRenderer,
        cellClass: "flex items-center",
      },
      {
        headerName: "Resignation Date",
        field: "resignationDate",
        minWidth: 40,
      },
      {
        headerName: "Last Working Day",
        field: "lastWorkingDay",
        minWidth: 40,
      },
      {
        headerName: "Personal Email",
        field: "personalEmail",
        minWidth: 400,
        editable: true,
        cellClass: "milestoneEditor",
        menuTabs: [],
      },
      {
        headerName: "Transfer to Personal Email",
        field: "transferAccessToPersonalEmail",
        minWidth: 140,
        editable: true,
        cellClass: "milestoneSelectEditor",
        menuTabs: [],
        valueGetter: (params) =>
          params.data?.transferAccessToPersonalEmail ? "Yes" : "No",
        valueSetter: (params) => {
          params.data.transferAccessToPersonalEmail = params.newValue === "Yes";
          return true;
        },
        cellEditorSelector: () => ({
          component: "agSelectCellEditor",
          popup: false,
        }),
        cellEditorParams: { values: ["No", "Yes"] },
      },
      {
        headerName: "Regenerate Credentials",
        field: "regenerateCredentials",
        minWidth: 140,
        editable: true,
        cellClass: "milestoneSelectEditor",
        menuTabs: [],
        valueGetter: (params) =>
          params.data?.regenerateCredentials ? "Yes" : "No",
        valueSetter: (params) => {
          params.data.regenerateCredentials = params.newValue === "Yes";
          return true;
        },
        cellEditorSelector: () => ({
          component: "agSelectCellEditor",
          popup: false,
        }),
        cellEditorParams: { values: ["No", "Yes"] },
      },
    ],
    [EmployeeRenderer]
  );

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      filter: true,
      resizable: true,
      minWidth: 100,
      menuTabs: [],
      filterParams: { buttons: [] },
    }),
    []
  );

  function collectEmployeeOptions(): BulkResignationEmployeeOption[] {
    return (gridApiRef.current?.getSelectedNodes() ?? []).map((node) => {
      const row = node.data as OptionRow;
      return {
        employeeId: row.employeeId,
        transferAccessToPersonalEmail: row.transferAccessToPersonalEmail,
        regenerateCredentials: row.regenerateCredentials,
        personalEmail: row.personalEmail || undefined,
      };
    });
  }

  function handleConfirmProceed() {
    setShowConfirmDialog(false);
    const selectedEmployeeIds = (
      gridApiRef.current?.getSelectedNodes() ?? []
    ).map((n) => (n.data as OptionRow).employeeId);
    bulkCompleteResignation(
      {
        employeeIds: selectedEmployeeIds,
        employeeOptions: collectEmployeeOptions(),
      },
      {
        onSuccess: (data: any) => {
          const failed: { employeeId: string; reason: string }[] =
            data?.failed ?? data?.data?.failed ?? [];
          if (failed.length) {
            setFailedEmployees(failed);
            toast(
              `Resignations failed for ${failed.length} employee${
                failed.length !== 1 ? "s" : ""
              }.`,
              { type: "error", autoClose: 5000 }
            );
            return;
          }
          toast("Resignations completed successfully!", {
            type: "success",
            autoClose: 2000,
          });
          isLeavingRef.current = true;
          navigate("/options/allEmployees");
        },
        onError: (err: any) => {
          toast(
            err?.response?.data?.reason ||
              err?.response?.data?.errorRaw ||
              "Something went wrong",
            { type: "error", autoClose: 5000 }
          );
        },
      }
    );
  }

  const employeesWithNonActiveGrants = employees
    .map((emp) => ({
      ...emp,
      filteredGrants: (emp.allGrants ?? []).filter(
        (g) =>
          g.state !== "OFFERED" &&
          g.state !== "OFFER_EXPIRED" &&
          g.state !== "GRANTED"
      ),
    }))
    .filter((e) => e.filteredGrants.length > 0);

  const hasNonActiveGrants = employeesWithNonActiveGrants.length > 0;

  const employeesWithPreview = employees.filter((e) => e.forfeiturePreview);
  const hasMissingMetadata = employeesWithPreview.some(
    (e) => e.forfeiturePreview?.hasMissingMetadata
  );

  function getAppliedRuleText(employee: EmployeeForResignation) {
    const preview = employee.forfeiturePreview;
    if (!preview) return "—";
    const isConditional = preview.ruleType === "CONDITIONAL";
    const selection = preview.ruleSelection ?? preview.ruleType;
    const resolutionText = preview.conditionalResolution
      ? preview.conditionalResolution === "EARLIER_DATE"
        ? "earlier date wins"
        : "later date wins"
      : null;
    const parts = [
      isConditional ? "Conditional rule" : "Default rule",
      preview.selectedConditionLabel,
      preview.matchedConditionsCount && preview.matchedConditionsCount > 1
        ? `${preview.matchedConditionsCount} matches`
        : null,
      preview.matchedConditionsSummary,
      selection === "CONDITIONAL" && resolutionText ? resolutionText : null,
    ].filter(Boolean);
    return parts.join(" · ");
  }

  function getResolvedExpiry(employee: EmployeeForResignation) {
    const preview = employee.forfeiturePreview;
    if (!preview?.resolvedExpiryDate) return "—";
    return preview.resolvedExpiryDate;
  }

  return (
    <VStack className="gap-4">
      <BCHeader
        className="items-baseline pb-4"
        bcTitle="Stakeholders"
        bcSubTitle="Bulk Complete Resignations"
        onClick={() => setShowDiscardDialog(true)}
      />
      <VStack className="w-full h-full px-4 bg-white border-2 rounded-md min-h-[600px]">
        {/* Header */}
        <HStack className="py-4 text-lg font-medium text-left bg-white border-b justify-between items-center flex-wrap gap-4">
          <VStack className="gap-0.5">
            <h6>Bulk Complete Resignations</h6>
            <p className="text-xs font-normal text-gray-500">
              Review and confirm options for {employees.length} employee
              {employees.length !== 1 ? "s" : ""}. Select rows to include in the
              completion.
            </p>
          </VStack>
          <div
            className={`flex items-center h-8 px-3 rounded gap-1 text-sm text-slate-dark bg-slate-light ${
              selectedCount === 0
                ? "opacity-40 cursor-not-allowed"
                : "cursor-pointer"
            }`}
            onClick={() => selectedCount > 0 && setShowBulkDialog(true)}
          >
            <span>
              Bulk Actions
              {selectedCount > 0 ? ` (${selectedCount} selected)` : ""}
            </span>
            <Icon icon="ri:arrow-drop-down-line" height={24} />
          </div>
        </HStack>

        {/* Grid */}
        <div
          className="py-4 ag-theme-material"
          style={{ height: Math.min(500, rowData.length * 48 + 100) }}
        >
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={48}
            headerHeight={40}
            rowSelection="multiple"
            suppressRowClickSelection
            rowClass="border-t border-dashed"
            singleClickEdit
            stopEditingWhenCellsLoseFocus
            onGridReady={(params) => {
              gridApiRef.current = params.api;
              params.api.sizeColumnsToFit();
              params.api.selectAll();
            }}
            onSelectionChanged={() => {
              setSelectedCount(
                gridApiRef.current?.getSelectedNodes().length ?? 0
              );
            }}
            onCellValueChanged={() => {
              const anyAction = (
                gridApiRef.current?.getRenderedNodes() ?? []
              ).some((node) => (node.data as OptionRow).regenerateCredentials);
              setHasEmailAction(anyAction);
            }}
          />
        </div>

        {/* Email disabled warning */}
        {hasEmailAction && emailSettings?.emailsToBeSent === false && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3 my-2">
            <Icon
              icon="ep:warning"
              color="#D97706"
              height={16}
              className="mt-0.5 shrink-0"
            />
            <span className="text-xs text-amber-800">
              Emails are currently disabled in settings. Regenerating
              credentials will generate new credentials, but no email will be
              sent to the employee. Do you want to continue?
            </span>
          </div>
        )}
        {hasEmailAction && emailSettings?.emailsToBeSent === true && (
          <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-3 my-2">
            <Icon
              icon="material-symbols:info-outline-rounded"
              color="#1D4ED8"
              height={16}
              className="mt-0.5 shrink-0"
            />
            <span className="text-xs text-blue-800">
              Emails are enabled in the settings. Employees with regenerate
              credentials enabled will receive email with their new credentials.
            </span>
          </div>
        )}

        {/* Failed employees */}
        {failedEmployees.length > 0 && (
          <HStack className="items-start gap-3 p-3 my-3 rounded-md border bg-red-50 border-red-200">
            <Icon
              icon="mdi:close-circle"
              width={20}
              height={20}
              className="text-red-500 mt-0.5 shrink-0"
            />
            <VStack className="gap-1">
              <p className="text-sm font-medium text-gray-800">
                {failedEmployees.length} employee
                {failedEmployees.length !== 1 ? "s" : ""} could not be processed
              </p>
              <ul className="list-disc list-inside space-y-0.5">
                {failedEmployees.map((f) => {
                  const emp = employees.find((e) => e.id === f.employeeId);
                  return (
                    <li key={f.employeeId} className="text-xs text-red-600">
                      <span className="text-gray-700 font-medium">
                        {emp?.employeeName ?? f.employeeId}
                      </span>
                      {" - "}
                      {f.reason}
                    </li>
                  );
                })}
              </ul>
            </VStack>
          </HStack>
        )}

        {/* Non-active grants warning */}
        {hasNonActiveGrants && (
          <VStack className="w-full gap-2 mb-3">
            <div className="flex items-start gap-2 bg-[#FFFBEB] border border-[#FDE68A] rounded p-3">
              <Icon
                icon="ep:warning"
                color="#D97706"
                height={16}
                className="mt-0.5 shrink-0"
              />
              <span className="italic text-xs text-[#92400E]">
                The following grants were either not in a granted state or not
                accepted, and will be deleted upon completing resignations.
              </span>
            </div>
            <table className="w-full border-collapse mt-1">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Employee
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Grant Info
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Grant State
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Effect on Grant
                  </th>
                </tr>
              </thead>
              <tbody>
                {employeesWithNonActiveGrants.flatMap((emp) =>
                  emp.filteredGrants.map((grant, gi) => (
                    <tr
                      key={`${emp.id}-${grant.identifier}-${gi}`}
                      className="border-b border-gray-100"
                    >
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {gi === 0 ? emp.employeeName : ""}
                      </td>
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {grant.identifier}
                      </td>
                      <td className="text-xs px-3 py-2 text-gray-700">
                        {grant.state}
                      </td>
                      <td className="text-xs px-3 py-2 italic text-gray-500">
                        Grant will be deleted
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </VStack>
        )}

        {/* Forfeiture rule preview */}
        {employeesWithPreview.length > 0 && (
          <VStack className="w-full gap-2 mb-3">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-semibold text-gray-700">
                Forfeiture Rule Preview
              </p>
              <p className="mt-1 text-xs text-gray-500">
                The preview reflects the expiry date calculated based on all
                matching conditions and the configured rule.
              </p>
            </div>

            {hasMissingMetadata && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-3">
                <Icon
                  icon="ep:warning"
                  color="#D97706"
                  height={16}
                  className="mt-0.5 shrink-0"
                />
                <span className="text-xs text-amber-800 italic">
                  Some employees have incomplete profile data (type / department
                  / designation). The default rule has been applied for them.
                </span>
              </div>
            )}

            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Employee
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Rule Applied
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Match Details
                  </th>
                  <th className="text-left text-xs font-semibold text-gray-600 px-3 py-2 border-b border-gray-200">
                    Resolved Expiry
                  </th>
                </tr>
              </thead>
              <tbody>
                {employeesWithPreview.map((emp) => (
                  <tr key={emp.id} className="border-b border-gray-100">
                    <td className="text-xs px-3 py-2 text-gray-700">
                      {emp.employeeName}
                      {emp.forfeiturePreview?.hasMissingMetadata && (
                        <span
                          title="Missing metadata — default rule applied"
                          className="ml-1 text-amber-500 cursor-help"
                        >
                          ⚠
                        </span>
                      )}
                    </td>
                    <td className="text-xs px-3 py-2">
                      <VStack className="gap-1">
                        <span
                          className={cn(
                            "w-fit px-2 py-0.5 rounded-full text-[10px] font-semibold",
                            emp.forfeiturePreview?.ruleType === "CONDITIONAL"
                              ? "bg-orange-50 text-orange-700"
                              : "bg-gray-100 text-gray-600"
                          )}
                        >
                          {emp.forfeiturePreview?.label}
                        </span>
                        {emp.forfeiturePreview?.conditionalResolution && (
                          <span className="text-[10px] text-gray-400">
                            {emp.forfeiturePreview.conditionalResolution ===
                            "EARLIER_DATE"
                              ? "Earlier date selected"
                              : "Later date selected"}
                          </span>
                        )}
                      </VStack>
                    </td>
                    <td className="text-xs px-3 py-2 text-gray-500">
                      {getAppliedRuleText(emp)}
                    </td>
                    <td className="text-xs px-3 py-2 text-gray-500">
                      {getResolvedExpiry(emp)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </VStack>
        )}

        {/* Footer */}
        <HStack className="justify-between gap-4 p-4 border-t mt-auto">
          <ButtonPrimary1
            type="reset"
            className="text-red-500 border-red-500 hover:bg-red-50"
            onClick={() => setShowDiscardDialog(true)}
          >
            Discard
          </ButtonPrimary1>
          <ButtonPrimary
            onClick={() => setShowConfirmDialog(true)}
            disabled={isLoading || selectedCount === 0}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Icon icon="eos-icons:loading" className="animate-spin" />
                <span>Confirming...</span>
              </div>
            ) : (
              `Complete Resignations (${selectedCount})`
            )}
          </ButtonPrimary>
        </HStack>

        <AlertDialog
          open={showConfirmDialog}
          message={`You are about to complete the resignation process for ${selectedCount} employee${
            selectedCount !== 1 ? "s" : ""
          }. This action cannot be undone.`}
          primaryActionText="Confirm"
          secondaryActionText="Cancel"
          onPrimaryAction={handleConfirmProceed}
          onSecondaryAction={() => setShowConfirmDialog(false)}
          onClose={() => setShowConfirmDialog(false)}
        />

        <AlertDialog
          open={showDiscardDialog}
          message="Are you sure you want to discard? All unsaved changes will be lost."
          primaryActionText="Discard"
          secondaryActionText="Cancel"
          onPrimaryAction={() => {
            isLeavingRef.current = true;
            navigate(-1);
          }}
          onSecondaryAction={() => setShowDiscardDialog(false)}
          onClose={() => setShowDiscardDialog(false)}
        />

        <AlertDialog
          open={showLeaveGuard}
          message="You have unsaved changes. Are you sure you want to leave this page?"
          primaryActionText="Leave"
          secondaryActionText="Stay"
          onPrimaryAction={handleLeave}
          onSecondaryAction={handleStay}
          onClose={handleStay}
        />

        {/* Bulk Actions popup */}
        <Dialog
          open={showBulkDialog}
          onClose={() => setShowBulkDialog(false)}
          maxWidth="xs"
        >
          <Box className="px-10 text-lg font-medium border-b py-7">
            <h6 className="flex justify-between items-center">
              Bulk Actions
              <span
                onClick={() => setShowBulkDialog(false)}
                className="cursor-pointer"
              >
                <Icon icon="material-symbols:close-rounded" />
              </span>
            </h6>
            <p className="text-xs font-normal text-gray-500 mt-1">
              {selectedCount > 0
                ? `Applies to ${selectedCount} selected employee${
                    selectedCount !== 1 ? "s" : ""
                  }`
                : "No employees selected"}
            </p>
          </Box>
          <VStack className="w-full px-10 py-7 gap-6">
            <VStack className="gap-8">
              <HStack className="justify-between gap-2 items-center">
                <Label>Transfer to Personal Email</Label>
                <Select
                  options={["No", "Yes"]}
                  value={bulkTransfer ? "Yes" : "No"}
                  isPlaceHolderDisabled
                  placeholder=""
                  className="w-32"
                  onChange={(e) =>
                    setBulkTransfer(
                      (e.target as HTMLSelectElement).value === "Yes"
                    )
                  }
                />
              </HStack>
              <HStack className="justify-between gap-2 items-center">
                <Label>Regenerate Credentials</Label>
                <Select
                  options={["No", "Yes"]}
                  value={bulkRegenerate ? "Yes" : "No"}
                  isPlaceHolderDisabled
                  placeholder=""
                  className="w-32"
                  onChange={(e) =>
                    setBulkRegenerate(
                      (e.target as HTMLSelectElement).value === "Yes"
                    )
                  }
                />
              </HStack>
            </VStack>
            <HStack className="justify-between">
              <ButtonPrimary1 onClick={() => setShowBulkDialog(false)}>
                Discard
              </ButtonPrimary1>
              <ButtonPrimary
                onClick={() => {
                  (gridApiRef.current?.getSelectedNodes() ?? []).forEach(
                    (node) => {
                      node.setDataValue(
                        "transferAccessToPersonalEmail",
                        bulkTransfer ? "Yes" : "No"
                      );
                      node.setDataValue(
                        "regenerateCredentials",
                        bulkRegenerate ? "Yes" : "No"
                      );
                    }
                  );
                  setShowBulkDialog(false);
                }}
              >
                Apply
              </ButtonPrimary>
            </HStack>
          </VStack>
        </Dialog>
      </VStack>
    </VStack>
  );
}

export default BulkCompleteResignations;
